import { inject, injectable } from 'inversify';
import EnhancedEventEmitter from '../EnhancedEventEmitter';
import { TOKEN } from '../inversify.tokens';
import { TrackLabel } from '../types/common';
import type { PeerTrackFactory } from '../types/container';
import PeerTrack from './media/tracks/PeerTrack';
import { PeerObserverEvents } from './Peer';
import PeerConsumer from './PeerConsumer';

export interface CreatePeerTrackParams {
  consumer?: PeerConsumer;
  label: TrackLabel;
  mediaStreamTrack: MediaStreamTrack;
  muted: boolean;
  paused: boolean;
  peerEventEmitter: EnhancedEventEmitter<PeerObserverEvents>;
}

@injectable()
export class RemotePeerTracks {
  private readonly tracksByConsumerId = new Map<PeerTrack['consumerId'], PeerTrack>();

  constructor(
    @inject(TOKEN.PeerTrackFactory) private readonly peerTrackFactory: PeerTrackFactory,
  ) { }

  register(consumerId: PeerTrack['consumerId'], track: PeerTrack): void {
    this.tracksByConsumerId.set(consumerId, track);
  }

  get(consumerId: PeerTrack['consumerId']): PeerTrack | undefined {
    return this.tracksByConsumerId.get(consumerId);
  }

  delete(consumerId: PeerTrack['consumerId']): boolean {
    return this.tracksByConsumerId.delete(consumerId);
  }

  reset(): void {
    this.tracksByConsumerId.clear();
  }

  create(params: CreatePeerTrackParams): PeerTrack {
    const {
      consumer,
      label,
      muted = false,
      mediaStreamTrack,
      paused,
      peerEventEmitter,
    } = params;

    const peerTrack = this.peerTrackFactory({
      consumer,
      label,
      mediaStreamTrack,
      paused,
      peerEventEmitter,
      muted,
      onClose: () => {
        if (peerTrack.consumerId !== undefined) {
          this.delete(peerTrack.consumerId);
        }
      },
    });

    if (peerTrack.consumerId !== undefined) { // only register remote tracks
      this.register(peerTrack.consumerId, peerTrack);
    }

    return peerTrack;
  }
}
